import React, { useCallback, useMemo, useState } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/ui/checkbox";
import { useNotification } from "@/context/NotificationContext";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useRoleStagePermissionMatrix } from "../../hooks/useRoleStagePermissionMatrix";
import {
  MatrixPermission,
  MatrixRole,
  MatrixStage,
  RoleCells,
} from "../../api/fetchRoleStagePermissionMatrix/types";
import { updateRoleStagePermissions } from "../../api/updateRoleStagePermissions";

const NO_STAGE_KEY = "no_stage";

interface MatrixColumn {
  key: string;
  name: string;
  hint?: string;
}

const sameIdSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

const cellsAreEqual = (a: RoleCells, b: RoleCells): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => sameIdSet(a[key] ?? [], b[key] ?? []));
};

const BusinessRolesList: React.FC = () => {
  const { stages, permissions, roles, loading, error } =
    useRoleStagePermissionMatrix();
  const { showNotification } = useNotification();

  const [expandedRoles, setExpandedRoles] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState("");
  const [cellsByRole, setCellsByRole] = useState<Record<number, RoleCells>>({});
  const [savedCellsByRole, setSavedCellsByRole] = useState<
    Record<number, RoleCells>
  >({});
  const [savingRoleId, setSavingRoleId] = useState<number | null>(null);

  // Hydrate editable state once the matrix arrives. Internal
  // (CertainPath-only) permissions never belong on client roles — they're
  // hidden from those grids, dropped from their cell state here, and the
  // backend strips them on save as well.
  React.useEffect(() => {
    const internalIds = new Set(
      permissions
        .filter((permission) => permission.isCertainPathOnly)
        .map((permission) => permission.id),
    );
    const initial: Record<number, RoleCells> = {};
    roles.forEach((role) => {
      if (role.isCertainPathOnly) {
        initial[role.id] = { ...role.cells };
        return;
      }
      const cleaned: RoleCells = {};
      Object.entries(role.cells).forEach(([columnKey, ids]) => {
        cleaned[columnKey] = ids.filter((id) => !internalIds.has(id));
      });
      initial[role.id] = cleaned;
    });
    setCellsByRole(initial);
    setSavedCellsByRole(initial);
  }, [roles, permissions]);

  const columns: MatrixColumn[] = useMemo(
    () => [
      ...[...stages]
        .sort((a, b) => a.position - b.position)
        .map((stage: MatrixStage) => ({ key: stage.uuid, name: stage.name })),
      { key: NO_STAGE_KEY, name: "No stage", hint: "Base permissions" },
    ],
    [stages],
  );

  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.label.localeCompare(b.label)),
    [roles],
  );

  const sortedPermissions = useMemo(
    () => [...permissions].sort((a, b) => a.label.localeCompare(b.label)),
    [permissions],
  );

  const visiblePermissions = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return sortedPermissions;
    return sortedPermissions.filter(
      (permission) =>
        permission.label.toLowerCase().includes(needle) ||
        permission.internalName.toLowerCase().includes(needle),
    );
  }, [sortedPermissions, filter]);

  // Client roles never see internal (CertainPath-only) permissions.
  const visibleClientPermissions = useMemo(
    () =>
      visiblePermissions.filter((permission) => !permission.isCertainPathOnly),
    [visiblePermissions],
  );

  const dirtyRoleIds = useMemo(
    () =>
      Object.keys(cellsByRole)
        .map(Number)
        .filter(
          (roleId) =>
            !cellsAreEqual(
              cellsByRole[roleId] ?? {},
              savedCellsByRole[roleId] ?? {},
            ),
        ),
    [cellsByRole, savedCellsByRole],
  );

  const toggleRole = (roleId: number) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const toggleCell = useCallback(
    (roleId: number, columnKey: string, permissionId: number) => {
      setCellsByRole((prev) => {
        const roleCells = prev[roleId] ?? {};
        const current = roleCells[columnKey] ?? [];
        const next = current.includes(permissionId)
          ? current.filter((id) => id !== permissionId)
          : [...current, permissionId];
        return { ...prev, [roleId]: { ...roleCells, [columnKey]: next } };
      });
    },
    [],
  );

  const setAllVisibleForColumn = useCallback(
    (roleId: number, columnKey: string, selectAll: boolean) => {
      const visibleIds = visiblePermissions.map((permission) => permission.id);
      setCellsByRole((prev) => {
        const roleCells = prev[roleId] ?? {};
        const current = roleCells[columnKey] ?? [];
        const next = selectAll
          ? [...new Set([...current, ...visibleIds])]
          : current.filter((id) => !visibleIds.includes(id));
        return { ...prev, [roleId]: { ...roleCells, [columnKey]: next } };
      });
    },
    [visiblePermissions],
  );

  const copyColumn = useCallback(
    (roleId: number, fromKey: string, toKey: string) => {
      setCellsByRole((prev) => {
        const roleCells = prev[roleId] ?? {};
        return {
          ...prev,
          [roleId]: { ...roleCells, [toKey]: [...(roleCells[fromKey] ?? [])] },
        };
      });
    },
    [],
  );

  const handleSaveRole = async (role: MatrixRole) => {
    try {
      setSavingRoleId(role.id);
      await updateRoleStagePermissions(role.id, {
        cells: cellsByRole[role.id] ?? {},
      });
      setSavedCellsByRole((prev) => ({
        ...prev,
        [role.id]: cellsByRole[role.id] ?? {},
      }));
      showNotification(
        `${role.label} saved`,
        "Members pick up permission changes on their next page load.",
        "success",
      );
    } catch {
      // Error handled by axios interceptor
    } finally {
      setSavingRoleId(null);
    }
  };

  return (
    <MainPageWrapper
      actions={
        <div className="flex items-center gap-3">
          {dirtyRoleIds.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Unsaved changes in {dirtyRoleIds.length}{" "}
              {dirtyRoleIds.length === 1 ? "role" : "roles"}
            </span>
          )}
          <button
            className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            onClick={() =>
              setExpandedRoles(new Set(sortedRoles.map((role) => role.id)))
            }
          >
            Expand All
          </button>
          <button
            className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            onClick={() => setExpandedRoles(new Set())}
          >
            Collapse All
          </button>
        </div>
      }
      error={error}
      loading={loading}
      subtitle="Each role's permissions per membership stage. The No stage column is what companies without a stage membership get; stage columns start empty until configured here."
      title="Business Roles & Permissions"
    >
      <div className="mb-4 max-w-sm">
        <SearchInput
          onChange={setFilter}
          placeholder="Filter permissions…"
          value={filter}
        />
      </div>

      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
        {sortedRoles.map((role, index) => (
          <RoleMatrixCard
            columns={columns}
            isDirty={dirtyRoleIds.includes(role.id)}
            isExpanded={expandedRoles.has(role.id)}
            isFirst={index === 0}
            key={role.id}
            onCopyColumn={(fromKey, toKey) =>
              copyColumn(role.id, fromKey, toKey)
            }
            onSave={() => handleSaveRole(role)}
            onSetAllVisible={(columnKey, selectAll) =>
              setAllVisibleForColumn(role.id, columnKey, selectAll)
            }
            onToggle={() => toggleRole(role.id)}
            onToggleCell={(columnKey, permissionId) =>
              toggleCell(role.id, columnKey, permissionId)
            }
            role={role}
            roleCells={cellsByRole[role.id] ?? {}}
            saving={savingRoleId === role.id}
            visiblePermissions={
              role.isCertainPathOnly
                ? visiblePermissions
                : visibleClientPermissions
            }
          />
        ))}
      </div>
    </MainPageWrapper>
  );
};

interface RoleMatrixCardProps {
  role: MatrixRole;
  columns: MatrixColumn[];
  roleCells: RoleCells;
  visiblePermissions: MatrixPermission[];
  isExpanded: boolean;
  isFirst: boolean;
  isDirty: boolean;
  saving: boolean;
  onToggle: () => void;
  onToggleCell: (columnKey: string, permissionId: number) => void;
  onSetAllVisible: (columnKey: string, selectAll: boolean) => void;
  onCopyColumn: (fromKey: string, toKey: string) => void;
  onSave: () => void;
}

const RoleMatrixCard: React.FC<RoleMatrixCardProps> = ({
  role,
  columns,
  roleCells,
  visiblePermissions,
  isExpanded,
  isFirst,
  isDirty,
  saving,
  onToggle,
  onToggleCell,
  onSetAllVisible,
  onCopyColumn,
  onSave,
}) => {
  const [copyFrom, setCopyFrom] = useState(columns[0]?.key ?? "");
  const [copyTo, setCopyTo] = useState(columns[1]?.key ?? "");

  const columnSelectionState = (
    columnKey: string,
  ): boolean | "indeterminate" => {
    if (visiblePermissions.length === 0) return false;
    const checked = roleCells[columnKey] ?? [];
    const count = visiblePermissions.filter((permission) =>
      checked.includes(permission.id),
    ).length;
    if (count === 0) return false;
    if (count === visiblePermissions.length) return true;
    return "indeterminate";
  };

  const selectClasses =
    "rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-gray-900 dark:text-slate-100";

  return (
    <div
      className={`bg-white dark:bg-slate-800 ${!isFirst ? "border-t border-gray-200 dark:border-slate-700" : ""}`}
    >
      <button
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
        onClick={onToggle}
      >
        <div className="text-left">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {role.label}
            </h3>
            {role.isCertainPathOnly && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 rounded">
                Internal
              </span>
            )}
            {isDirty && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded">
                Unsaved
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {role.internalName}
          </p>
        </div>
        {isExpanded ? (
          <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-slate-700/50">
          {role.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
              {role.description}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 mt-3 mb-3">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>Copy</span>
              <select
                aria-label="Copy from column"
                className={selectClasses}
                onChange={(e) => setCopyFrom(e.target.value)}
                value={copyFrom}
              >
                {columns.map((column) => (
                  <option key={column.key} value={column.key}>
                    {column.name}
                  </option>
                ))}
              </select>
              <span>to</span>
              <select
                aria-label="Copy to column"
                className={selectClasses}
                onChange={(e) => setCopyTo(e.target.value)}
                value={copyTo}
              >
                {columns.map((column) => (
                  <option key={column.key} value={column.key}>
                    {column.name}
                  </option>
                ))}
              </select>
              <button
                className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
                disabled={copyFrom === copyTo || saving}
                onClick={() => onCopyColumn(copyFrom, copyTo)}
              >
                Apply
              </button>
            </div>
            <Button disabled={!isDirty || saving} onClick={onSave} size="sm">
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <th className="px-4 py-2.5 text-left font-normal text-gray-500 dark:text-gray-400">
                    Permission
                  </th>
                  {columns.map((column) => (
                    <th
                      className="px-3 py-2.5 text-center font-normal text-gray-900 dark:text-white"
                      key={column.key}
                    >
                      <span className="inline-flex flex-col items-center gap-1.5">
                        <span>
                          {column.name}
                          {column.hint && (
                            <span className="block text-[10px] font-normal text-gray-400 dark:text-gray-500">
                              {column.hint}
                            </span>
                          )}
                        </span>
                        <Checkbox
                          aria-label={`Select all visible permissions for ${column.name}`}
                          checked={columnSelectionState(column.key)}
                          disabled={saving || visiblePermissions.length === 0}
                          onCheckedChange={() =>
                            onSetAllVisible(
                              column.key,
                              columnSelectionState(column.key) !== true,
                            )
                          }
                          size="sm"
                          title="Select all"
                        />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                {visiblePermissions.map((permission) => (
                  <tr key={permission.id}>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                      <span className="inline-flex items-center gap-2">
                        {permission.label}
                        {permission.isCertainPathOnly && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 rounded">
                            Internal
                          </span>
                        )}
                      </span>
                    </td>
                    {columns.map((column) => (
                      <td className="px-3 py-2 text-center" key={column.key}>
                        <Checkbox
                          aria-label={`${permission.label} for ${column.name}`}
                          checked={(roleCells[column.key] ?? []).includes(
                            permission.id,
                          )}
                          disabled={saving}
                          onCheckedChange={() =>
                            onToggleCell(column.key, permission.id)
                          }
                          size="sm"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {visiblePermissions.length === 0 && (
                  <tr>
                    <td
                      className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                      colSpan={columns.length + 1}
                    >
                      No permissions match the filter
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessRolesList;
